/**
 * Product Type Display Component
 * Displays product type with icon and description
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { ProductType, ProductTypeConfig } from '@/types/inventory';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ProductTypeDisplayProps {
  productType: ProductType;
  showDescription?: boolean;
  variant?: 'default' | 'inline' | 'badge';
  className?: string;
}

export function ProductTypeDisplay({
  productType,
  showDescription = false,
  variant = 'default',
  className
}: ProductTypeDisplayProps) {
  // Get product config with fallback for undefined or missing types
  const config = ProductTypeConfig[productType] || {
    icon: '📦',
    label: productType || 'Unknown Product',
    abbreviation: 'UNK',
    description: 'Unknown product type',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300'
  };

  if (variant === 'badge') {
    return (
      <Badge variant="outline" className={cn('gap-2', className)}>
        <span>{config.icon}</span>
        <span>{config.abbreviation}</span>
      </Badge>
    );
  }

  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-2', className)}>
        <span>{config.icon}</span>
        <span className="font-medium">{config.label}</span>
      </span>
    );
  }

  return (
    <div className={cn('flex items-start gap-3', className)}>
      <span className="text-2xl">{config.icon}</span>
      <div>
        <div className="font-medium text-gray-900">{config.label}</div>
        <div className="text-sm text-gray-500">{config.abbreviation}</div>
        {showDescription && (
          <div className="text-xs text-gray-400 mt-1">{config.description}</div>
        )}
      </div>
    </div>
  );
}
